import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/features/authentication/hooks/useAuth';
import { apiClient } from '@/core/api/client';
import { useRecaptchaGuestSession } from '@/features/guest-session/hooks/useRecaptchaGuestSession';
import { logChatResponse } from '@/core/services/chatSession.service';
import { clearFpQaHandoff, getFpQaHandoff } from '@/shared/utils/fpQaHandoff';
import { getChatUserType, sendChatMessage } from '@/core/services/chatService';
import type { ChatRequestPayload, ChatResponsePayload } from '@/core/services/chatService';
import { startGeneration } from '@/shared/components/navigator-results/generation/navigatorGenerationStore';
import { trackRumAction } from '@/shared/observability/rum';
import type { ChatMessage } from '../types/navigator-chat.types';
import {
  GENERATE_CARE_GUIDE_MESSAGE,
  careSeekerGreeting,
  fpGreeting,
  resolveCompletionAction,
  saveGeneratingSnapshot,
} from './navigatorChatCompletion';
import type { CompletionContext } from './navigatorChatCompletion';
import { restoreNavigatorConversation } from './restoreNavigatorConversation';

const CHAT_SESSION_STORAGE_KEY = 'chat_session_id';

const staticChatResponse: ChatResponsePayload = {
  session_id: 'id_007',
  response: '',
  status: 'in_progress',
  classification: null,
};

export const useNavigatorChat = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { recaptchaElement, ensureGuestSession } = useRecaptchaGuestSession();
  // Latched at mount: the generation store clears the FP handoff once the
  // engagement is created, and the chat (still mounted, showing the Generate
  // Care Guide message) must not flip to care-seeker copy mid-conversation.
  const [isFpMode] = useState(() => Boolean(getFpQaHandoff()));
  // Set when the intake completes — drives the Generate Care Guide message,
  // locks the composer, and carries the state the loading page needs.
  const [completion, setCompletion] = useState<CompletionContext | null>(null);
  const [careStageData, setCareStageData] = useState<Record<string, unknown> | null>(null);
  const [chatResponse, setChatResponse] = useState<ChatResponsePayload>(staticChatResponse);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  // True when the mount restored a saved conversation (a returning user
  // resuming an in-progress chat) — drives the "Start a new chat" affordance.
  const [isResumed, setIsResumed] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const hasUserInteractedRef = useRef(false);
  // Version guard: each send captures the current id, and startNewChat bumps it.
  // An in-flight send whose id is stale (because the user started a new chat
  // mid-request) must not mutate messages/session/typing on the fresh chat.
  const sendRunIdRef = useRef(0);
  const isClassificationConfirmed =
    chatResponse.status === 'confirmed' || chatResponse.status === 'classification_ready';

  useEffect(() => {
    if (!hasUserInteractedRef.current) return;
    scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [input]);

  useEffect(() => {
    const greeting = isFpMode ? fpGreeting : careSeekerGreeting;
    const run = async () => {
      setIsResumed(false);
      const restored = await restoreNavigatorConversation(isAuthenticated, setSessionId);
      setMessages(restored.messages ?? [greeting]);
      setIsResumed(restored.isResumed);
    };
    void run();
  }, [isAuthenticated, isFpMode]);

  const appendError = (content: string) =>
    setMessages((previous) => [...previous, { id: `${Date.now()}-error`, role: 'assistant', content }]);

  const handleCompletion = async (responseData: ChatResponsePayload, nextSessionId: string | null) => {
    const targetSessionId = nextSessionId ?? '';
    const targetCareStage = responseData.classification?.care_stage ?? '';
    const fpHandoff = getFpQaHandoff();

    if (!fpHandoff) {
      sessionStorage.setItem('bquest_nav_session_id', targetSessionId);
      sessionStorage.setItem('bquest_nav_care_stage', targetCareStage);
    }
    await logChatResponse({
      user_id: user?.id ?? null,
      session_id: responseData.session_id,
      response: responseData.response,
      status: responseData.status,
      classification: responseData.classification,
      care_stage_data: responseData.care_stage_data,
    });

    const action = resolveCompletionAction(responseData, targetSessionId, fpHandoff);
    if (action.type === 'concierge') {
      if (fpHandoff) clearFpQaHandoff();
      navigate('/care-navigator/concierge');
      return;
    }
    if (action.type === 'continue') return;

    // Persist a durable fallback for /care-navigator/generating BEFORE
    // kicking generation: a reload (or a direct hit on the loading page URL)
    // drops router state, and — unlike care-seeker — nothing else persists
    // the FP's session/mode/engagement payload.
    saveGeneratingSnapshot({
      ...action.context,
      mode: action.startOptions.mode,
      fpEngagement: action.startOptions.fpEngagement,
    });
    // Kick the full Care Guide generation NOW, in the background — journey
    // summary, matchmaking, resource prefetches, and (FP) the engagement
    // create. The Generate Care Guide button below is a visual affordance:
    // by the time the user clicks it, the work is already underway.
    startGeneration(action.context.sessionId, action.startOptions);
    setCompletion(action.context);
    setMessages((previous) => [
      ...previous,
      {
        id: `${Date.now()}-generate`,
        role: 'assistant',
        kind: 'generate-care-guide',
        content: GENERATE_CARE_GUIDE_MESSAGE,
      },
    ]);
  };

  // The button inside the generate-care-guide message. Generation is already
  // running — this only moves the user to the guided loading page.
  const goToGenerating = () => {
    if (!completion) return;
    trackRumAction('care_guide_generate_button_click', { sessionId: completion.sessionId });
    navigate('/care-navigator/generating', {
      state: { ...completion, mode: isFpMode ? 'fp' : 'care-seeker' },
    });
  };

  const handleSend = async (text?: string) => {
    const messageText = text || input.trim();
    // A completed conversation is over — quick replies and sends are inert.
    if (!messageText || isTyping || completion) return;
    hasUserInteractedRef.current = true;
    const runId = ++sendRunIdRef.current;
    const isCurrent = () => runId === sendRunIdRef.current;

    const updatedConversation: ChatMessage[] = [
      ...messages,
      { id: `${Date.now()}`, role: 'user', content: messageText },
    ];
    const requestPayload: ChatRequestPayload = {
      session_id: sessionId,
      status: chatResponse.status || 'IN_PROGRESS',
      user_type: getChatUserType(user?.role),
      message: messageText,
      confirmed: true,
      care_stage_data: careStageData || null,
      conversation: updatedConversation.map((m) => ({ role: m.role, content: m.content })),
    };

    setMessages(updatedConversation);
    setInput('');
    setIsTyping(true);

    try {
      await ensureGuestSession(isAuthenticated);
      const responseData = await sendChatMessage(requestPayload);
      // Bail if the user started a new chat while this request was in flight —
      // otherwise a stale response would repopulate the freshly-reset chat.
      if (!isCurrent()) return;
      setCareStageData(responseData.care_stage_data ?? null);
      const nextSessionId = responseData.session_id || sessionId;

      if (nextSessionId) {
        const completeConversation = [
          ...requestPayload.conversation,
          ...(responseData.response
            ? [{ role: 'assistant' as const, content: responseData.response }]
            : []),
        ];
        void apiClient
          .post('/chat-engine/store-request', {
            ...requestPayload,
            session_id: nextSessionId,
            conversation: completeConversation,
          })
          .catch((err: unknown) => console.error('[ChatEngine] Failed to store conversation:', err));
      }

      setChatResponse(responseData);
      setSessionId(nextSessionId);
      if (
        nextSessionId &&
        responseData.status !== 'completed' &&
        responseData.status !== 'classification_ready'
      ) {
        localStorage.setItem(CHAT_SESSION_STORAGE_KEY, nextSessionId);
      }
      if (responseData.response) {
        const quickReplies = responseData.quick_reply_options
          ? responseData.quick_reply_options.split(',').map((item: string) => item.trim())
          : [];
        setMessages((previous) => [
          ...previous,
          { id: `${Date.now()}-assistant`, role: 'assistant', content: responseData.response, quickReplies },
        ]);
      }

      if (
        responseData.status === 'completed' ||
        responseData.status === 'classification_ready' ||
        responseData.status === 'concierge_routed'
      ) {
        await handleCompletion(responseData, nextSessionId);
      }
    } catch (error) {
      console.error('Failed to send chat message:', error);
      // Don't surface a stale send's error on the fresh chat.
      if (isCurrent()) {
        appendError('Something went wrong while contacting the care assistant. Please try again.');
      }
    } finally {
      // Only the current send owns the typing indicator; a stale send must not
      // flip it off (or on) for the fresh chat.
      if (isCurrent()) setIsTyping(false);
    }
  };

  // Discard resumed/in-progress chat and start fresh, in place. Can't reuse
  // navigatorSession.startNewCarePlan here: it navigates to /care-navigator and
  // relies on a remount reading `force_new_chat`, but user already on
  // /care-navigator (no remount), so we reset state directly while clearing
  // `chat_session_id` + `bquest_nav_session_id`.
  const startNewChat = () => {
    // Invalidate any in-flight send so its late response can't mutate the fresh
    // chat (race guard — see sendRunIdRef / handleSend).
    sendRunIdRef.current += 1;
    localStorage.removeItem(CHAT_SESSION_STORAGE_KEY);
    sessionStorage.removeItem('bquest_nav_session_id');
    hasUserInteractedRef.current = false;
    setSessionId(null);
    setChatResponse(staticChatResponse);
    setCareStageData(null);
    setInput('');
    setIsTyping(false);
    setIsResumed(false);
    setCompletion(null);
    setMessages([isFpMode ? fpGreeting : careSeekerGreeting]);
  };

  return {
    isFpMode,
    messages,
    input,
    setInput,
    isTyping,
    isClassificationConfirmed,
    isResumed,
    isCompleted: completion !== null,
    startNewChat,
    scrollRef,
    textareaRef,
    recaptchaElement,
    handleSend,
    goToGenerating,
    isAuthenticated,
    goToDashboard: () => navigate('/dashboard/financial-advisor'),
    goToNavigatorDashboard: () => navigate('/care-navigator/dashboard'),
  };
};
